import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { RadioTower, Users, ShieldAlert, CheckCircle2, Activity, Baby } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO = "https://content.webfactorysite.co.uk/20260113092111-Wales-Event-Logo.png?t=1768296071";

export default function LiveEvent() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetch(`${API}/public/event-live/${token}`, { credentials: "omit" })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((d) => alive && setData(d))
        .catch(() => alive && setError("This live view link is not valid or has expired."));
    load();
    const t = setInterval(load, 15000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [token]);

  useEffect(() => {
    if (data) document.title = `Live — ${data.event} — Wales Event Medical`;
    return () => { document.title = "Wales Event Medical — Staff Portal"; };
  }, [data]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0F2547] grid place-items-center p-6">
        <div className="bg-white rounded-2xl p-8 max-w-sm text-center" data-testid="live-error">
          <p className="font-heading font-bold text-[#0F2547] text-lg mb-2">Live view unavailable</p>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0F2547] grid place-items-center">
        <div className="h-10 w-10 rounded-full border-4 border-[#38BDF8] border-t-transparent animate-spin" />
      </div>
    );
  }

  const tiles = [
    { key: "with_us", label: "Patients with us now", icon: Users, color: "text-[#38BDF8]", testid: "live-count-with-us" },
    { key: "high", label: "High priority", icon: ShieldAlert, color: "text-[#FF6B5E]", testid: "live-count-high" },
    { key: "discharged", label: "Discharged", icon: CheckCircle2, color: "text-[#4ADE80]", testid: "live-count-discharged" },
    { key: "treated", label: "Patients treated (total)", icon: Activity, color: "text-white", testid: "live-count-treated" },
  ];

  return (
    <div className="min-h-screen bg-[#0F2547] text-white flex flex-col" data-testid="live-screen">
      <header className="flex items-center justify-between px-5 sm:px-10 py-5 border-b border-slate-800">
        <div className="flex items-center gap-4">
          <div className="bg-white rounded-lg p-2">
            <img src={LOGO} alt="Wales Event Medical Services" className="h-8 sm:h-10 w-auto" />
          </div>
          <div>
            <h1 className="font-heading font-extrabold text-lg sm:text-2xl leading-tight" data-testid="live-event-name">{data.event}</h1>
            <p className="text-xs sm:text-sm text-slate-400">
              {data.venue}{data.venue && data.event_date && " · "}{data.event_date}
            </p>
          </div>
        </div>
        <span className="inline-flex items-center gap-2 bg-red-600/20 border border-red-500/50 text-red-400 text-xs font-bold px-3 py-1.5 rounded-full" data-testid="live-badge">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
          </span>
          LIVE
        </span>
      </header>

      <main className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 sm:p-6 content-center">
        {tiles.map(({ key, label, icon: Icon, color, testid }) => (
          <div key={key} className="bg-[#173766]/60 border border-slate-700/60 rounded-xl p-3 sm:p-4 flex flex-col items-center justify-center text-center">
            <Icon className={`h-5 w-5 sm:h-6 sm:w-6 mb-1.5 ${color}`} />
            <p className={`font-vitals font-extrabold text-3xl sm:text-5xl tracking-tight ${color}`} data-testid={testid}>
              {data.counts[key]}
            </p>
            <p className="text-slate-300 text-[10px] sm:text-xs font-semibold mt-1 uppercase tracking-widest">{label}</p>
          </div>
        ))}
      </main>

      <section className="mx-4 sm:mx-6 mb-3 sm:mb-4 bg-[#173766]/60 border border-slate-700/60 rounded-xl px-4 py-3 flex items-center justify-center gap-5 sm:gap-10" data-testid="live-lost-children">
        <div className="flex items-center gap-2">
          <Baby className="h-4 w-4 text-[#38BDF8]" />
          <h2 className="font-heading font-extrabold text-[11px] sm:text-xs uppercase tracking-widest text-slate-200">Lost children</h2>
        </div>
        <div className="text-center">
          <p className="font-vitals font-extrabold text-2xl sm:text-3xl text-white" data-testid="live-lost-total">{data.counts.lost_total}</p>
          <p className="text-slate-300 text-[9px] font-semibold uppercase tracking-widest">Logged</p>
        </div>
        <div className="text-center">
          <p className="font-vitals font-extrabold text-2xl sm:text-3xl text-[#38BDF8]" data-testid="live-lost-with-us">{data.counts.lost_with_us}</p>
          <p className="text-slate-300 text-[9px] font-semibold uppercase tracking-widest">With us</p>
        </div>
        <div className="text-center">
          <p className="font-vitals font-extrabold text-2xl sm:text-3xl text-[#4ADE80]" data-testid="live-lost-reunited">{data.counts.lost_reunited}</p>
          <p className="text-slate-300 text-[9px] font-semibold uppercase tracking-widest">Reunited</p>
        </div>
      </section>

      <footer className="px-5 sm:px-10 py-4 border-t border-slate-800 flex items-center justify-between text-[11px] sm:text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <RadioTower className="h-3.5 w-3.5" /> Updates automatically every 15 seconds
        </span>
        <span data-testid="live-updated-at">
          Last updated {new Date(data.updated_at).toLocaleTimeString("en-GB")}
        </span>
      </footer>
    </div>
  );
}
